import { ConversationStatus } from "constants/ChatListingConstants";
import { AgentViewTypes } from "constants/ChatViewConstants";
import ChatExpirationService from "services/ChatExpirationService";
import { uepClient } from "uepClient";
import {
  NEW_CONVERSATION,
  NEW_MESSAGE,
  SET_ACTIVE_CONVERSATION,
  DISPOSE_CONVERSATION,
  UPDATE_CONVERSATION_META,
  INITIALIZE_CONVERSATIONS,
  SET_CLOSED_CONVERSATIONS,
  SET_CLOSED_CONVERSATION_MESSAGES,
  LOAD_OLD_CONVERSATIONS,
  TRANSFER_CONVERSATION,
  CHANGE_AGENT_VIEW,
  INITIALIZE_TRANSFERRED_CONVERSATIONS,
  SET_TRANSFERRED_CONVERSATION_MESSAGES,
  SET_DELIVERY_INFO_IN_MESSAGE,
  SET_SEEN_INFO_IN_MESSAGE,
  UPDATE_SUGGESTIONS_FOR_MESSAGE,
  SET_OPEN_CONVERSATION_MESSAGES,
} from "./types";

const fetchAllClientChatData = (
  conversation,
  dispatch,
  includeCurrentConvo
) => {
  Promise.all([
    getAndSetConversationMetaIfExistingUser(conversation, dispatch),
    getClientHistoricalChats(conversation, dispatch, includeCurrentConvo),
  ])
    .then((responses) => {
      const conversationMetaData = responses[0];
      const clientHistoricalChatData = responses[1];

      if (conversationMetaData && conversationMetaData !== "") {
        const conversationMeta = {
          phoneNumber: conversationMetaData.phone,
          customerId: conversationMetaData.customerId,
          name: conversationMetaData.name,
          email: conversationMetaData.email,
          city: conversationMetaData.city,
          state: conversationMetaData.state,
          pinCode: conversationMetaData.pincode,
          channel: conversationMetaData.channel,
          valid: conversationMetaData.valid,
          title: conversationMetaData.title,
        };
        dispatch({
          type: UPDATE_CONVERSATION_META,
          payload: {
            conversationId: conversation.conversationId,
            conversationMeta,
          },
        });
      }

      if (clientHistoricalChatData) {
        let conversationMeta;
        if (!conversationMetaData || conversationMetaData === "") {
          conversationMeta = {
            customerId: conversation.conversationMeta.customerId,
            phoneNumber: conversation.conversationMeta.phoneNumber,
            clientId: null,
            name: null,
            email: null,
            city: null,
            state: null,
            pinCode: null,
            channel: conversation.conversationMeta.channel,
            title: null,
          };
        } else {
          conversationMeta = {
            customerId: conversationMetaData.customerId,
            phoneNumber: conversationMetaData.phone,
            clientId: conversationMetaData.id,
            name: conversationMetaData.name,
            email: conversationMetaData.email,
            city: conversationMetaData.city,
            state: conversationMetaData.state,
            pinCode: conversationMetaData.pincode,
            channel: conversationMetaData.channel,
            title: conversationMetaData.title,
          };
        }
        const olderClientConversations = clientHistoricalChatData;
        const oldConversations = [];
        olderClientConversations.forEach((conv) => {
          if (conv.conversationId === conversation.conversationId) {
            ChatExpirationService.messagesForChatLoaded(conv);
          }

          oldConversations.push({
            conversationId: conv.conversationId,
            agentId: conv.agentId,
            agentName: `${conv.agentFirstName} ${conv.agentLastName}`,
            status: conv.status,
            messages: conv.messages,
            conversationMeta: conversationMeta,
            chatTransferDetails: conv.chatTransferDetails,
          });
        });
        dispatch({
          type: LOAD_OLD_CONVERSATIONS,
          payload: oldConversations,
        });
      }
    })
    .catch((err) => {
      console.error(err.message);
    });
};

const getAndSetConversationMetaIfExistingUser = (conversation) => {
  const conversationCustomerId = conversation.conversationMeta.customerId;

  return new Promise((resolve, reject) => {
    uepClient()
      .get(`/external-client/${conversationCustomerId}`)
      .then((res) => {
        return resolve(res.data);
      })
      .catch((err) => {
        console.error("No Conversation Meta to Update", err.message);
        reject(err);
      });
  });
};

const getClientHistoricalChats = (conversation, includeCurrentConvo) => {
  const conversationCustomerId = conversation.conversationMeta.customerId;

  return new Promise((resolve, reject) => {
    uepClient()
      .post(`/chat-message/external-client/search`, {
        customerId: conversationCustomerId,
        excludedConversations: includeCurrentConvo
          ? []
          : [conversation.conversationId],
      })
      .then((response) => {
        return resolve(response.data);
      })
      .catch((error) => {
        console.error(
          "No historical chats obtained for this number: ",
          conversationCustomerId
        );
        reject(error);
      });
  });
};

export const initiateNewConversation = (
  conversation,
  includeCurrentConvo = false
) => (dispatch) => {
  Promise.resolve(
    dispatch({
      type: NEW_CONVERSATION,
      payload: conversation,
    })
  ).then(() => {
    fetchAllClientChatData(conversation, dispatch, includeCurrentConvo);
  });
};

export const addNewMessage = (message) => (dispatch) => {
  dispatch({
    type: NEW_MESSAGE,
    payload: message,
  });
};

export const setActiveConversation = ({ conversationId, status }) => (
  dispatch
) => {
  dispatch({
    type: SET_ACTIVE_CONVERSATION,
    payload: { conversationId, status },
  });
};

export const disposeCurrentConversation = (conversation) => (dispatch) => {
  dispatch({
    type: DISPOSE_CONVERSATION,
    payload: conversation,
  });
  dispatch({
    type: SET_ACTIVE_CONVERSATION,
    payload: null,
  });
};

export const initializeConversations = (conversations) => (dispatch) => {
  dispatch({
    type: INITIALIZE_CONVERSATIONS,
    payload: conversations,
  });
};

export const setClosedConversations = (closedConversations) => (dispatch) => {
  dispatch({
    type: SET_CLOSED_CONVERSATIONS,
    payload: closedConversations,
  });
};

export const setClosedConversationMessages = ({ conversationId, messages }) => (
  dispatch
) => {
  dispatch({
    type: SET_CLOSED_CONVERSATION_MESSAGES,
    payload: { conversationId, messages },
  });
};

export const transferConversation = (conversation) => (dispatch) => {
  dispatch({
    type: TRANSFER_CONVERSATION,
    payload: conversation,
  });
  dispatch({
    type: SET_ACTIVE_CONVERSATION,
    payload: null,
  });
  dispatch({
    type: CHANGE_AGENT_VIEW,
    payload: AgentViewTypes.DEFAULT,
  });
};

export const initializeTransferredConversations = (agentId) => (dispatch) => {
  uepClient()
    .get(`agent-conversation/transferred-conversations/${agentId}`)
    .then((res) => {
      const transferredConversationData = res.data;
      const transferredConversations = transferredConversationData.map(
        (con) => ({
          conversationId: con.conversationId,
          status: ConversationStatus.TRANSFERRED,
          messages: [],
          conversationMeta: {
            customer: con.customerId,
            phoneNumber: con.phoneNumber,
            name: con.customerName,
            email: con.customerEmail,
            city: con.customerCity,
            state: con.customerState,
            pinCode: con.customerPincode,
            channel: con.channel,
            title: con.customerTitle,
          },
          chatTransferDetails: [],
          agentId: con.transferredToAgentId,
          agentName: `${con.transferredToAgentFirstName} ${con.transferredToAgentLastName}`,
        })
      );
      dispatch({
        type: INITIALIZE_TRANSFERRED_CONVERSATIONS,
        payload: transferredConversations,
      });
    });
};

export const setTransferredConversationMessages = (
  conversationId,
  messages
) => (dispatch) => {
  dispatch({
    type: SET_TRANSFERRED_CONVERSATION_MESSAGES,
    payload: {
      conversationId,
      messages,
    },
  });
};

export const updateMessageWithDeliveryInfo = (message) => (dispatch) => {
  dispatch({
    type: SET_DELIVERY_INFO_IN_MESSAGE,
    payload: message,
  });
};

export const updateMessageWithSeenInfo = (message) => (dispatch) => {
  dispatch({
    type: SET_SEEN_INFO_IN_MESSAGE,
    payload: message,
  });
};

export const updateMessageWithSuggestions = (message) => (dispatch) => {
  dispatch({
    type: UPDATE_SUGGESTIONS_FOR_MESSAGE,
    payload: message,
  });
};

// Called when an agent opens a chat card for a conversation that only has a summary
// (last message) loaded. Fetches full message history + old conversations from the backend.
export const fetchConversationDetails = (conversationId) => (dispatch) => {
  uepClient()
    .get(`/chat-message/conversation/${conversationId}/details`)
    .then((res) => {
      const { client, conversations } = res.data;
      const activeConv = conversations.find((c) => c.conversationId === conversationId);
      const oldConvs = conversations.filter((c) => c.conversationId !== conversationId);

      if (activeConv) {
        ChatExpirationService.messagesForChatLoaded(activeConv);
        dispatch({
          type: SET_OPEN_CONVERSATION_MESSAGES,
          payload: {
            conversationId,
            messages: activeConv.messages,
            chatTransferDetails: activeConv.chatTransferDetails || [],
          },
        });
      }

      if (oldConvs.length && client) {
        const conversationMeta = {
          customerId: client.customerId,
          phoneNumber: client.phone || "",
          clientId: client.id,
          name: client.name,
          email: client.email,
          city: client.city,
          state: client.state,
          pinCode: client.pincode,
          channel: client.channel,
          title: client.title,
        };
        const oldConversationsForRedux = oldConvs.map((conv) => ({
          conversationId: conv.conversationId,
          agentId: conv.agentId,
          agentName: [conv.agentFirstName, conv.agentLastName].filter(Boolean).join(" "),
          status: conv.status,
          messages: conv.messages,
          conversationMeta,
          chatTransferDetails: conv.chatTransferDetails || [],
        }));
        dispatch({
          type: LOAD_OLD_CONVERSATIONS,
          payload: oldConversationsForRedux,
        });
      }
    })
    .catch((err) => {
      console.error(`Failed to fetch details for conversation ${conversationId}`, err);
    });
};
